import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { exerciseLibrary, equipment as equipmentApi } from '../../services/api';
import { safeMediaUrl } from '../WorkoutPlayer/playerLogic';
import { Chip, Modal, Thumb, labelOf, useDebounced } from './shared';
import './ExerciseLibrary.css';

/*
 * A biblioteca de exercícios vista pelo sócio ("Treino → Exercícios"): pesquisar, filtrar e abrir a ficha de cada exercício.
 *
 * O que o sócio vê é só o que o servidor lhe dá: exercícios ativos e ficheiros APROVADOS (um vídeo pendente ou rejeitado nunca
 * chega aqui). Quando não há vídeo aprovado mostra-se a imagem; quando não há nenhuma, diz-se que ainda não há demonstração —
 * nunca se inventa uma. As imagens de terceiros mostram a atribuição e a licença, como a licença exige.
 */

const PAGE_SIZE = 18;
const INITIAL = { q: '', group: '', level: '', type: '', mechanic: '', pattern: '', equipmentId: '', onlyAvailable: false, demo: '' };

const toParams = (f, page) => {
  const p = { page, pageSize: PAGE_SIZE, sort: 'name' };
  if (f.q.trim()) p.q = f.q.trim();
  ['group', 'level', 'type', 'mechanic', 'pattern', 'equipmentId'].forEach((k) => { if (f[k]) p[k] = f[k]; });
  if (f.onlyAvailable) p.onlyAvailable = true;
  if (f.demo) p.demo = f.demo;
  return p;
};

const ASSET_CHIP = { complete: 'Imagem e demonstração', demo: 'Demonstração', image: 'Imagem' };

function Credit({ media }) {
  if (!media || (!media.attribution && !media.license)) return null;
  const href = safeMediaUrl(media.sourceUrl);
  return (
    <p className="lib-hint" data-testid="media-credit" style={{ margin: '4px 0 0' }}>
      {media.attribution}{media.attribution && media.license ? ' · ' : ''}{media.license}
      {href ? <> · <a href={href} target="_blank" rel="noopener noreferrer">origem</a></> : null}
    </p>
  );
}

function MediaStage({ media, name }) {
  const [index, setIndex] = useState(0);
  const [brokenVideo, setBrokenVideo] = useState(false);

  // O vídeo próprio primeiro (é a demonstração), depois o vídeo incorporado (só quando não há próprio) e as imagens.
  // Um vídeo que falha cai para a imagem.
  const items = useMemo(() => {
    const usable = (media || []).filter((m) => safeMediaUrl(m.url));
    const videos = usable.filter((m) => m.kind === 'video');
    const embeds = videos.length > 0 ? [] : usable.filter((m) => m.kind === 'embed');
    const photos = usable.filter((m) => m.kind !== 'video' && m.kind !== 'embed')
      .sort((a, b) => (b.isPrimary - a.isPrimary) || (a.sortOrder - b.sortOrder));
    return [...videos, ...embeds, ...photos];
  }, [media]);

  useEffect(() => { setIndex(0); setBrokenVideo(false); }, [items]);

  const visible = items.filter((m) => !(m.kind === 'video' && brokenVideo));
  const current = visible[Math.min(index, visible.length - 1)];
  if (!current) {
    return <div className="lib-empty" data-testid="no-media">Este exercício ainda não tem imagem nem demonstração.</div>;
  }

  return (
    <div>
      <div style={{ background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
        {current.kind === 'video'
          ? (
            <video key={current.id} src={safeMediaUrl(current.url)} controls muted loop playsInline preload="metadata"
              aria-label={current.altText || `Demonstração de ${name}`} style={{ width: '100%', maxHeight: 360, display: 'block' }}
              onError={() => setBrokenVideo(true)} />
          )
          : current.kind === 'embed'
            // Vídeo de terceiros no player oficial: nada foi copiado. Se não carregar (sem rede), fica o endereço.
            ? (
              <div>
                <iframe key={current.id} src={safeMediaUrl(current.url)} title={current.altText ? `Vídeo do YouTube: ${current.altText}` : `Demonstração de ${name} (YouTube)`}
                  loading="lazy" referrerPolicy="strict-origin-when-cross-origin"
                  allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                  style={{ width: '100%', aspectRatio: '16 / 9', maxHeight: 360, border: 0, display: 'block' }} />
                <p className="lib-hint" style={{ margin: 0, padding: '6px 8px' }} data-testid="member-embed-credit">
                  {current.channelName ? `${current.channelName} · ` : ''}Vídeo do YouTube · conteúdo de terceiros, incorporado.
                  {safeMediaUrl(current.sourceUrl) ? <a href={safeMediaUrl(current.sourceUrl)} target="_blank" rel="noopener noreferrer"> Abrir no YouTube</a> : null}
                </p>
              </div>
            )
            : <img key={current.id} src={safeMediaUrl(current.url)} alt={current.altText || name} style={{ width: '100%', maxHeight: 360, objectFit: 'contain', display: 'block' }} />}
      </div>
      <Credit media={current} />
      {visible.length > 1 && (
        <div className="lib-chips" style={{ marginTop: 8 }} role="tablist" aria-label="Imagens e demonstrações">
          {visible.map((m, i) => (
            <button key={m.id} type="button" role="tab" aria-selected={i === index} className={`lib-btn small${i === index ? ' primary' : ''}`}
              onClick={() => setIndex(i)}>
              {m.kind === 'video' ? 'Vídeo' : m.kind === 'embed' ? 'Vídeo do YouTube' : `Imagem ${visible.filter((x) => x.kind !== 'video' && x.kind !== 'embed').indexOf(m) + 1}`}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Fiche({ id, meta, onClose, onOpen }) {
  const [state, setState] = useState({ loading: true, error: null, detail: null });

  useEffect(() => {
    let alive = true;
    setState({ loading: true, error: null, detail: null });
    exerciseLibrary.get(id)
      .then(({ data }) => { if (alive) setState({ loading: false, error: null, detail: data }); })
      .catch(() => { if (alive) setState({ loading: false, error: 'Não foi possível abrir o exercício.', detail: null }); });
    return () => { alive = false; };
  }, [id]);

  const d = state.detail;
  const primary = (d?.muscles || []).filter((m) => m.role === 'primary');
  const secondary = (d?.muscles || []).filter((m) => m.role === 'secondary');
  const rx = d && d.defaultSets
    ? `${d.defaultSets} × ${d.defaultRepsMin === d.defaultRepsMax || !d.defaultRepsMax ? d.defaultRepsMin : `${d.defaultRepsMin}–${d.defaultRepsMax}`}${d.defaultRestSeconds ? ` · descanso ${d.defaultRestSeconds} s` : ''}`
    : null;

  return (
    <Modal title={d?.name || 'Exercício'} onClose={onClose}>
      {state.loading && <div className="lib-loading" role="status">A carregar…</div>}
      {state.error && <div className="lib-notice bad" role="alert">{state.error}</div>}
      {d && (
        <div data-testid="member-fiche">
          <MediaStage media={d.media} name={d.name} />

          <div className="lib-chips" style={{ margin: '12px 0' }}>
            {d.groupLabel && <Chip tone="accent">{d.groupLabel}</Chip>}
            {d.levelLabel && <Chip>{d.levelLabel}</Chip>}
            {d.mechanic && <Chip>{labelOf(meta.mechanics, d.mechanic)}</Chip>}
            {d.movementPattern && <Chip>{labelOf(meta.patterns, d.movementPattern)}</Chip>}
            {d.exerciseType && <Chip>{d.exerciseTypeLabel || labelOf(meta.exerciseTypes, d.exerciseType)}</Chip>}
          </div>

          {d.description && <p style={{ marginTop: 0 }}>{d.description}</p>}

          {(primary.length > 0 || secondary.length > 0) && (
            <section aria-label="Músculos">
              <h3 style={{ fontSize: '1rem', marginBottom: 6 }}>Músculos</h3>
              {primary.length > 0 && <p style={{ margin: '0 0 4px' }}><strong>Principais:</strong> {primary.map((m) => m.name).join(', ')}</p>}
              {secondary.length > 0 && <p style={{ margin: 0 }}><strong>Secundários:</strong> {secondary.map((m) => m.name).join(', ')}</p>}
            </section>
          )}

          {d.equipmentLinks?.length > 0 && (
            <section aria-label="Equipamento">
              <h3 style={{ fontSize: '1rem', margin: '14px 0 6px' }}>Equipamento</h3>
              <div className="lib-chips">
                {d.equipmentLinks.map((e) => (
                  <Chip key={e.id} tone={e.isAvailable ? 'ok' : 'warn'} title={e.isAvailable ? 'O ginásio tem esta máquina' : 'Este ginásio não tem esta máquina'}>
                    {e.name}{e.isAvailable ? '' : ' (não disponível)'}
                  </Chip>
                ))}
              </div>
            </section>
          )}

          {d.steps?.length > 0 && (
            <section aria-label="Como fazer">
              <h3 style={{ fontSize: '1rem', margin: '14px 0 6px' }}>Como fazer</h3>
              <ol style={{ margin: 0, paddingLeft: 20 }}>{d.steps.map((s, i) => <li key={`${i}-${s.slice(0, 12)}`} style={{ marginBottom: 4 }}>{s}</li>)}</ol>
            </section>
          )}

          {d.mistakes?.length > 0 && (
            <section aria-label="Erros comuns">
              <h3 style={{ fontSize: '1rem', margin: '14px 0 6px' }}>Erros comuns</h3>
              <ul style={{ margin: 0, paddingLeft: 20 }}>{d.mistakes.map((s, i) => <li key={`${i}-${s.slice(0, 12)}`} style={{ marginBottom: 4 }}>{s}</li>)}</ul>
            </section>
          )}

          {d.tip && <div className="lib-notice" style={{ marginTop: 14 }}><strong>Dica:</strong> {d.tip}</div>}
          {rx && <p className="lib-hint" style={{ marginTop: 8 }}>Prescrição habitual: {rx}.</p>}

          {d.substitutes?.length > 0 && (
            <section aria-label="Variantes e alternativas">
              <h3 style={{ fontSize: '1rem', margin: '14px 0 6px' }}>Variantes e alternativas</h3>
              <div className="lib-chips">
                {d.substitutes.filter((s) => s.isActive !== false).map((s) => (
                  <button key={s.exerciseId} type="button" className="lib-btn small" onClick={() => onOpen(s.exerciseId)}>
                    {s.name}{s.available ? '' : ' (sem máquina)'}
                  </button>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </Modal>
  );
}

const EMPTY_META = { groups: [], levels: [], mechanics: [], patterns: [], exerciseTypes: [] };
let metaOnce = null;   // o vocabulário é o mesmo para toda a sessão: pede-se uma vez

/**
 * A ficha de UM exercício, para abrir de qualquer ecrã (Hoje, Plano, biblioteca): clicar numa linha de exercício mostra o mesmo
 * que a biblioteca mostra — demonstração, músculos, equipamento, passos, erros comuns, alternativas. As alternativas abrem
 * na mesma janela. Só chega o que o servidor dá ao sócio (ficheiros aprovados).
 */
export function ExerciseFicheDialog({ exerciseId, onClose }) {
  const [id, setId] = useState(exerciseId);
  const [meta, setMeta] = useState(EMPTY_META);

  useEffect(() => { setId(exerciseId); }, [exerciseId]);
  useEffect(() => {
    let alive = true;
    if (!metaOnce) metaOnce = Promise.resolve().then(() => exerciseLibrary.meta()).then(({ data }) => data).catch(() => { metaOnce = null; return EMPTY_META; });
    metaOnce.then((data) => { if (alive) setMeta({ ...EMPTY_META, ...data }); });
    return () => { alive = false; };
  }, []);

  if (id == null) return null;
  return <Fiche id={id} meta={meta} onClose={onClose} onOpen={setId} />;
}

export default function MemberLibrary() {
  const [meta, setMeta] = useState(null);
  const [machines, setMachines] = useState([]);
  const [filters, setFilters] = useState(INITIAL);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const requestRef = useRef(0);

  const q = useDebounced(filters.q, 250);
  // A chave é o conteúdo: digitar muda `filters.q` a cada letra, mas só o texto já "assentado" conta para a pesquisa.
  const effectiveKey = JSON.stringify({ ...filters, q });
  const effective = useMemo(() => JSON.parse(effectiveKey), [effectiveKey]);

  useEffect(() => {
    exerciseLibrary.meta().then(({ data }) => setMeta(data)).catch(() => setMeta({ groups: [], levels: [], mechanics: [], patterns: [], exerciseTypes: [] }));
    equipmentApi.search({ pageSize: 100, onlyAvailable: true }).then(({ data }) => setMachines(data.items || [])).catch(() => setMachines([]));
  }, []);

  const fetchPage = useCallback((nextPage, append) => {
    const request = ++requestRef.current;
    setLoading(true);
    exerciseLibrary.search(toParams(effective, nextPage))
      .then(({ data }) => {
        if (request !== requestRef.current) return;
        setItems((cur) => (append ? [...cur, ...data.items] : data.items));
        setTotal(data.total);
        setPage(nextPage);
        setError(null);
      })
      .catch(() => { if (request === requestRef.current) setError('Não foi possível carregar os exercícios.'); })
      .finally(() => { if (request === requestRef.current) setLoading(false); });
  }, [effective]);

  useEffect(() => { fetchPage(1, false); }, [fetchPage]);

  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const filtered = JSON.stringify(filters) !== JSON.stringify(INITIAL);
  const m = meta || { groups: [], levels: [], mechanics: [], patterns: [], exerciseTypes: [] };

  return (
    <div className="lib-page" style={{ padding: 0 }} data-testid="member-library">
      <div className="lib-filters">
        <div className="lib-filters-top">
          <input className="lib-input" type="search" value={filters.q} onChange={(e) => set({ q: e.target.value })}
            placeholder="Procurar por nome, músculo ou máquina…" aria-label="Procurar exercícios" />
          <button type="button" className="lib-btn lib-filter-toggle" aria-expanded={filtersOpen} onClick={() => setFiltersOpen((o) => !o)}>Filtros</button>
        </div>

        <div className={`lib-filter-grid${filtersOpen ? ' open' : ''}`}>
          <select className="lib-select" aria-label="Grupo muscular" value={filters.group} onChange={(e) => set({ group: e.target.value })}>
            <option value="">Grupo</option>
            {m.groups.map((g) => <option key={g.code} value={g.code}>{g.label}</option>)}
          </select>
          <select className="lib-select" aria-label="Dificuldade" value={filters.level} onChange={(e) => set({ level: e.target.value })}>
            <option value="">Dificuldade</option>
            {m.levels.map((g) => <option key={g.code} value={g.code}>{g.label}</option>)}
          </select>
          <select className="lib-select" aria-label="Movimento" value={filters.pattern} onChange={(e) => set({ pattern: e.target.value })}>
            <option value="">Movimento</option>
            {m.patterns.map((g) => <option key={g.code} value={g.code}>{g.label}</option>)}
          </select>
          <select className="lib-select" aria-label="Composto ou isolado" value={filters.mechanic} onChange={(e) => set({ mechanic: e.target.value })}>
            <option value="">Mecânica</option>
            {m.mechanics.map((g) => <option key={g.code} value={g.code}>{g.label}</option>)}
          </select>
          <select className="lib-select" aria-label="Tipo" value={filters.type} onChange={(e) => set({ type: e.target.value })}>
            <option value="">Tipo</option>
            {(m.exerciseTypes || []).map((g) => <option key={g.code} value={g.code}>{g.label}</option>)}
          </select>
          <select className="lib-select" aria-label="Máquina ou equipamento" value={filters.equipmentId} onChange={(e) => set({ equipmentId: e.target.value })}>
            <option value="">Máquina</option>
            {machines.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
          <select className="lib-select" aria-label="Demonstração" value={filters.demo} onChange={(e) => set({ demo: e.target.value })}>
            <option value="">Demonstração</option>
            <option value="approved">Só com demonstração em vídeo</option>
          </select>
          <div className="lib-filter-checks">
            <label className="lib-check"><input type="checkbox" checked={filters.onlyAvailable} onChange={(e) => set({ onlyAvailable: e.target.checked })} />Só o que o ginásio tem</label>
            {filtered && <button type="button" className="lib-btn small ghost" onClick={() => setFilters(INITIAL)}>Limpar filtros</button>}
          </div>
        </div>
      </div>

      {error && <div className="lib-notice bad" role="alert">{error}</div>}

      <div aria-live="polite">
        {loading && items.length === 0 && <div className="lib-loading" role="status">A carregar…</div>}
        {!loading && !error && items.length === 0 && (
          <div className="lib-empty">
            {filtered ? 'Nenhum exercício com estes filtros.' : 'A biblioteca ainda não tem exercícios.'}
            {filtered && <div style={{ marginTop: 8 }}><button type="button" className="lib-btn small" onClick={() => setFilters(INITIAL)}>Limpar filtros</button></div>}
          </div>
        )}
      </div>

      <div className="lib-list" style={{ opacity: loading && items.length > 0 ? 0.6 : 1 }}>
        {items.map((item) => (
          <button key={item.id} type="button" className="lib-card" onClick={() => setOpenId(item.id)} aria-label={`${item.name} — ver ficha`}>
            <Thumb src={safeMediaUrl(item.thumbUrl) || safeMediaUrl(item.imageUrl)} alt="" />
            <span className="lib-card-body">
              <span className="lib-card-title">{item.name}</span>
              <span className="lib-chips">
                {item.groupLabel && <Chip tone="accent">{item.groupLabel}</Chip>}
                {item.levelLabel && <Chip>{item.levelLabel}</Chip>}
                {ASSET_CHIP[item.assetState] && item.assetState !== 'image' && <Chip tone="ok">{ASSET_CHIP[item.assetState]}</Chip>}
                {!item.available && <Chip tone="warn" title="Falta, neste ginásio, uma máquina de que o exercício precisa">Sem máquina</Chip>}
              </span>
              <span className="lib-card-meta">
                {item.equipmentLinks?.length > 0 ? item.equipmentLinks.map((e) => e.name).join(' · ') : 'Sem equipamento'}
              </span>
            </span>
          </button>
        ))}
      </div>

      {total > 0 && (
        <div className="lib-pager">
          <span>{items.length} de {total} {total === 1 ? 'exercício' : 'exercícios'}</span>
          {items.length < total && (
            <button type="button" className="lib-btn small" disabled={loading} onClick={() => fetchPage(page + 1, true)}>Ver mais</button>
          )}
        </div>
      )}

      {openId != null && <Fiche id={openId} meta={m} onClose={() => setOpenId(null)} onOpen={(id) => setOpenId(id)} />}
    </div>
  );
}
